export function createAuthHeadersFromCookies(cookies: any) {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  console.log("[createAuthHeaders] Cookies:", cookies); // Debug
  
  // Extract SITEACTIVEID from multiple possible sources
  let siteActiveId: string | null = null;
  let isReactNative = false;
  let authToken: string | null = null;
  
  // First try to get it from go-debug-headers (React Native case)
  if (cookies["go-debug-headers"]) {
    try {
      const debugHeaders = JSON.parse(cookies["go-debug-headers"]);
      isReactNative = true;
      
      if (debugHeaders["x-go-siteactiveid"]) {
        siteActiveId = debugHeaders["x-go-siteactiveid"];
        console.log("Found SITEACTIVEID in debug headers:", siteActiveId);
      }
      
      if (debugHeaders["x-go-auth-token"]) {
        authToken = debugHeaders["x-go-auth-token"];
      }
      
      // Add all debug headers to the request except siteactiveid (we'll handle it separately)
      for (const [key, value] of Object.entries(debugHeaders)) {
        if (key.startsWith("x-go-") && key !== "x-go-siteactiveid") {
          const normalizedKey = key.replace("x-go-", "x-");
          headers[normalizedKey] = String(value);
          
          // Also add without the x- prefix for compatibility
          if (normalizedKey.startsWith("x-")) {
            const legacyKey = normalizedKey.substring(2);
            headers[legacyKey] = String(value);
          }
        }
      }
    } catch (e) {
      console.warn("[createAuthHeaders] Failed to parse go-debug-headers:", e);
    }
  }
  
  // If not found in debug headers, try direct cookie
  if (!siteActiveId && cookies["SITEACTIVEID"]) {
    siteActiveId = cookies["SITEACTIVEID"];
  }
  
  // Set SITEACTIVEID in all required header formats if found
  if (siteActiveId) {
    console.log(`[createAuthHeaders] Found SITEACTIVEID: ${siteActiveId}`);
    
    // React Native case - use both cookie and direct headers
    if (isReactNative) {
      // Special handling for React Native to ensure all auth formats are included
      // First remove any existing SITEACTIVEID from cookies to prevent duplication
      const cookiePairs = Object.entries(cookies)
        .filter(([key]) => key !== "go-debug-headers" && key !== "SITEACTIVEID") // Exclude debug headers and existing SITEACTIVEID
        .map(([name, value]) => `${name}=${value}`);
        
      // Then add SITEACTIVEID once
      cookiePairs.push(`SITEACTIVEID=${siteActiveId}`);
      
      // Join the cookie pairs
      headers["cookie"] = cookiePairs.join("; ");
      
      // Set SITEACTIVEID header only once - use uppercase as backend expects
      headers["SITEACTIVEID"] = siteActiveId;
      
      // If we have an auth token, include it in various formats
      if (authToken) {
        headers["auth-token"] = authToken;
        headers["authorization"] = `Bearer ${authToken}`;
      }
    } else {
      // Browser case - construct cookie string with existing cookies (except SITEACTIVEID)
      // and add SITEACTIVEID once
      const cookiePairs = Object.entries(cookies)
        .filter(([key]) => key !== "SITEACTIVEID") // Filter out existing SITEACTIVEID
        .map(([name, value]) => `${name}=${value}`);
      
      // Add SITEACTIVEID once
      cookiePairs.push(`SITEACTIVEID=${siteActiveId}`);
      
      headers["cookie"] = cookiePairs.join("; ");
      // Set SITEACTIVEID header only once - use uppercase as backend expects
      headers["SITEACTIVEID"] = siteActiveId;
    }
  } else {
    console.warn('[createAuthHeaders] No SITEACTIVEID found in any location');
    
    // Fallback: generate a regular cookie string, ensuring no duplicate cookies
    const uniqueCookies = new Map();
    
    // Add all cookies to the map (this will automatically handle duplicates)
    for (const [name, value] of Object.entries(cookies)) {
      uniqueCookies.set(name, value);
    }
    
    // Convert the map back to a cookie string
    const cookieString = Array.from(uniqueCookies.entries())
      .map(([name, value]) => `${name}=${value}`)
      .join("; ");

    headers["cookie"] = cookieString;
  }

  return headers;
}

/**
 * Returns SITEACTIVEID from cookies or go-debug-headers.
 * @param cookies Object containing cookies
 * @returns SITEACTIVEID value or null
 */
export function getSiteActiveIdFromCookies(
  cookies: Record<string, any>
): string | null {
  console.log('[getSiteActiveIdFromCookies] Checking cookies:', cookies);

  // First check for direct SITEACTIVEID
  if (cookies['SITEACTIVEID']) {
    console.log('[getSiteActiveIdFromCookies] Found direct SITEACTIVEID:', cookies['SITEACTIVEID']);
    return cookies['SITEACTIVEID'];
  }

  // Debug header case
  if (cookies["go-debug-headers"]) {
    try {
      const debugHeaders = JSON.parse(cookies["go-debug-headers"]);
      if (debugHeaders["x-go-siteactiveid"]) {
        console.log('[getSiteActiveIdFromCookies] Found SITEACTIVEID in debug headers:', debugHeaders["x-go-siteactiveid"]);
        return debugHeaders["x-go-siteactiveid"];
      }
    } catch (e) {
      console.warn(
        "[getSiteActiveIdFromCookies] Failed to parse go-debug-headers:",
        e
      );
    }
  } else {
    // Fallback: find SITEACTIVEID in cookies
    for (const [name, value] of Object.entries(cookies)) {
      if (name === "SITEACTIVEID") {
        return value;
      }
    }
  }
  return null;
}

/**
 * Returns true if go-debug-headers contains X-GO-2FA-BIOMETRICS and its value is true
 * @param cookies Object containing cookies
 * @returns boolean
 */
export function is2FABiometric(cookies: Record<string, any>): boolean {
  if (cookies["go-debug-headers"]) {
    try {
      const debugHeaders = JSON.parse(cookies["go-debug-headers"]);

      return debugHeaders["x-go-2fa-biometrics"] == "true";
    } catch (e) {
      // Invalid JSON, treat as not 2FA biometric
      return false;
    }
  }
  return false;
}
